import React, { useEffect, useRef, useState } from 'react'
import Modal from 'components/modal'
import Button from 'components/button'
import { Folder, Projects } from 'components/icons'
import { useHubFolders } from 'hooks/useHubFolders'
import { useProject } from 'hooks/useProject'
import { IHubFolder, ISidebarMoveItem } from 'types'
import { SidebarNode, TOP_LEVEL_LABEL, canNestFoldersIn, findNode } from 'components/sidebar/components/folders/tree'

interface MoveToModalProps {
  /** What moves, as a move request names it (dragGroup's order and ancestor rules). */
  items: ISidebarMoveItem[]
  /** The item's name, for the title when only one moves. */
  itemName: string
  /** The sidebar's tree, for where things are now and the folders to offer. */
  tree: SidebarNode[]
  /** Cancelled. */
  onClose: () => void
  /** The server accepted the move to `destination` (null = top level); the parent closes the modal. */
  onMoved: (destination: string | null) => void
}

/** A place the items could go: the top level (null) or a folder, at its depth in the tree. */
export interface MoveDestination {
  id: string | null
  name: string
  depth: number
  /** Everything moving is already here, so there's nowhere to move: shown, but can't be chosen. */
  current: boolean
}

/**
 * The places `items` could move to, top level first and then every folder in sidebar order, each
 * at its depth. Left out: the folders a drag couldn't drop the moving folders into (themselves, their
 * subfolders, anywhere they'd nest deeper than the limit). A place is current only when every item is
 * already in it.
 */
const moveDestinations = (tree: SidebarNode[], folders: IHubFolder[], items: ISidebarMoveItem[]): MoveDestination[] => {
  const parents = new Set(items.map((item) => findNode(tree, item.id)?.parentId))
  const isCurrent = (id: string | null): boolean => parents.size === 1 && parents.has(id)
  const folderIds = items.filter((item) => item.kind === 'folder').map((item) => item.id)
  const fits = (id: string): boolean => canNestFoldersIn(folders, folderIds, id)

  const walk = (nodes: SidebarNode[]): MoveDestination[] =>
    nodes.flatMap((node) => {
      // Everything under a folder that can't take them can't either.
      if (node.kind !== 'folder' || !fits(node.id)) return []
      return [
        { id: node.id, name: node.name, depth: node.depth + 1, current: isCurrent(node.id) },
        ...walk(node.children)
      ]
    })
  return [{ id: null, name: TOP_LEVEL_LABEL, depth: 0, current: isCurrent(null) }, ...walk(tree)]
}

/** "Move 'Spec'" for one item, "Move 3 items" for several. */
const moveTitle = (count: number, itemName: string): string =>
  count === 1 ? `Move '${itemName}'` : `Move ${count} items`

interface DestinationTreeProps {
  destinations: MoveDestination[]
  value: string | null | undefined
  onChange: (id: string | null) => void
}

/**
 * The destinations as an indented tree, one row per place: a listbox where the arrow keys move the
 * choice, skipping the current location, which is marked and can't be chosen.
 */
const DestinationTree: React.FC<DestinationTreeProps> = ({ destinations, value, onChange }) => {
  const listRef = useRef<HTMLDivElement>(null)
  const choosable = destinations.filter((d) => !d.current)
  // The one row in the tab order: the chosen place, or else the first that can be chosen.
  const tabStop = value !== undefined ? value : choosable[0]?.id

  // Focus starts in the dialog, on the tree.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[role="option"][tabindex="0"]')?.focus()
  }, [])

  const choose = (id: string | null): void => {
    onChange(id)
    listRef.current?.querySelector<HTMLElement>(`[data-destination="${id ?? ''}"]`)?.focus()
  }

  // The first press chooses the row focus starts on; after that they move the choice.
  const step = (by: 1 | -1): void => {
    if (value === undefined && tabStop !== undefined) {
      choose(tabStop)
      return
    }
    const at = choosable.findIndex((d) => d.id === tabStop)
    const next = choosable[Math.min(choosable.length - 1, Math.max(0, at + by))]
    if (next) choose(next.id)
  }

  return (
    <div
      ref={listRef}
      role="listbox"
      tabIndex={-1}
      aria-label="Destination folder"
      className="flex flex-col max-h-72 overflow-y-auto rounded-md border border-divider p-1"
      onKeyDown={(event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
        event.preventDefault()
        step(event.key === 'ArrowDown' ? 1 : -1)
      }}
    >
      {destinations.map((destination) => {
        const selected = destination.id === value
        return (
          <div
            key={destination.id ?? ''}
            role="option"
            aria-selected={selected}
            aria-disabled={destination.current || undefined}
            tabIndex={destination.id === tabStop && !destination.current ? 0 : -1}
            data-testid="move-to-destination"
            data-destination={destination.id ?? ''}
            className={`flex items-center gap-2 h-8 pr-2 rounded text-[13px] font-medium outline-none ${
              destination.current
                ? 'text-muted cursor-default'
                : 'text-body cursor-pointer hover:bg-surface-hover focus-visible:bg-surface-hover'
            } ${selected ? 'bg-surface-hover' : ''}`}
            style={{ paddingLeft: 8 + destination.depth * 16 }}
            onClick={() => {
              if (!destination.current) choose(destination.id)
            }}
            onKeyDown={(event) => {
              if (destination.current || (event.key !== 'Enter' && event.key !== ' ')) return
              event.preventDefault()
              choose(destination.id)
            }}
          >
            {destination.id === null ? <Projects /> : <Folder />}
            <span className="min-w-0 flex-1 truncate">{destination.name}</span>
            {destination.current && <span className="shrink-0 text-xs text-muted">Current location</span>}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Sidebar "Move to…": pick a destination folder, or the top level, and the items go to the end of
 * it. Where the items already are is marked and can't be picked (moving there would only shuffle them
 * to the end). A refused move has already been shown and undone by useHubFolders, so the modal just
 * stays open; if the refusal was because the destination has gone, the refresh takes it out of the
 * tree and the choice with it.
 */
const MoveToModal: React.FC<MoveToModalProps> = ({ items, itemName, tree, onClose, onMoved }) => {
  const { moveItems } = useHubFolders()
  const { project } = useProject()
  // undefined until the user picks; null is the top level.
  const [destination, setDestination] = useState<string | null | undefined>(undefined)
  const [moving, setMoving] = useState(false)
  const destinations = moveDestinations(tree, project.hubFolders, items)

  // A destination a refresh has taken away (deleted, or no longer visible) is no longer chosen.
  if (destination !== undefined && !destinations.some((d) => d.id === destination && !d.current)) {
    setDestination(undefined)
  }

  const move = (): void => {
    if (destination === undefined) return
    setMoving(true)
    moveItems(items, destination, null).then((result) => {
      setMoving(false)
      if (result.ok) onMoved(destination)
    })
  }

  if (items.length === 0) return null

  return (
    <Modal
      id="sidebar-move-to-modal"
      open
      setOpen={(open) => {
        if (!open) onClose()
      }}
      width="small"
      title={moveTitle(items.length, itemName)}
    >
      <DestinationTree destinations={destinations} value={destination} onChange={setDestination} />
      <div className="flex justify-end mt-5">
        <Button
          data-testid="sidebar-move-to-submit"
          internalType="primary"
          onClick={move}
          disabled={destination === undefined || moving}
          isLoading={moving}
        >
          Move
        </Button>
      </div>
    </Modal>
  )
}

export default MoveToModal
